import Vue from "vue";
import firebase from "firebase";
import firebaseApp from "@/main";
import Vuex, { GetterTree, MutationTree, ActionTree } from "vuex";

// Interfaces
import {
  formatWorkoutDocId,
  SubmitWorkoutData,
  UpdateWorkoutData,
  Workout,
} from "@/interfaces/workout.interface";
import {
  Exercise,
  formatExerciseId,
  formatExerciseVideoId,
} from "@/interfaces/exercise.interface";
import { userData, userWorkoutTemplate } from "@/interfaces/user.interface";
import moment from "moment";

Vue.use(Vuex);

class State {
  currentWorkout: Workout | null = null;
  currentWorkoutDialogOpen = false;
}

const getters = <GetterTree<State, any>>{
  isUserCurrentlyWorkingOut(state): boolean {
    return state.currentWorkout !== null ? true : false;
  },
  getCurrentWorkout(state): Workout | null {
    return state.currentWorkout;
  },
  // ! Adds random 12 hours
  getTimeSpentWorkingOut(state): string {
    const workout: Workout | null = state.currentWorkout;

    if (workout) {
      console.log(String(moment().unix() - workout.startTime));
      return String(moment().unix() - workout.startTime);
    }
    return "0";
  },
  isCurrentWorkoutDialogCollapsed(state): boolean {
    return state.currentWorkoutDialogOpen;
  },
};

const mutations = <MutationTree<State>>{
  clearCurrentWorkout(state) {
    state.currentWorkout = null;
  },
  updateCurrentWorkout(state, newWorkoutData: UpdateWorkoutData) {
    if (state.currentWorkout) {
      if (newWorkoutData.workoutName)
        state.currentWorkout.workoutName = newWorkoutData.workoutName;
      if (newWorkoutData.startTime)
        state.currentWorkout.startTime = newWorkoutData.startTime;
      if (newWorkoutData.exercises)
        state.currentWorkout.exercises = newWorkoutData.exercises;
    }
  },
  setCurrentWorkout(state, newWorkout: Workout | null) {
    state.currentWorkout = newWorkout;
  },
  changeCurrentWorkoutDialogCollapse(state, collapse: boolean) {
    state.currentWorkoutDialogOpen = collapse;
  },
};

const actions = <ActionTree<State, any>>{
  async submitWorkout({ rootGetters }, payload: SubmitWorkoutData) {
    const myUID: string | null = rootGetters["userStore/getUserUID"];
    const userData: userData | null = rootGetters["userStore/getUserData"];

    if (!myUID || !userData) return Promise.reject("NOT SIGNED IN");

    let suc = true;
    const batch = firebaseApp.firestore().batch();

    const formatedExercies: Exercise[] = payload.formatedExercies;
    const workoutData: Workout = payload.workoutData;
    if (payload.templateId) workoutData.templateId = payload.templateId;

    const userDocRef = firebaseApp
      .firestore()
      .collection("users")
      .doc(myUID);

    // Remove the current workout that user is doing
    userData.currentWorkout = null;
    batch.update(userDocRef, userData);

    const docTitle = `${workoutData.startTime}`;

    // Upload users videos, Get video url
    // use videoUrl to set exercise.sets[#].videoUrl property
    const allVideoLocations: string[] = [];
    for (const exercise in formatedExercies) {
      for (const set in formatedExercies[exercise].sets) {
        const exerciseSets = formatedExercies[exercise].sets;
        const exerciseVideoBlob: Blob | null | undefined =
          exerciseSets[set].videoBlob;

        if (exerciseVideoBlob) {
          const videoLocation = `users/${myUID}/workouts/exercises/${formatExerciseId(
            formatedExercies[exercise].exerciseName,
            formatedExercies[exercise].type
          )}/${formatExerciseVideoId(workoutData.startTime, set)}`;

          allVideoLocations.push(videoLocation);

          await firebaseApp
            .storage()
            .ref(videoLocation)
            .put(exerciseVideoBlob)
            .then(async (res) => {
              await res.ref
                .getDownloadURL()
                .then((downloadUrl) => {
                  formatedExercies[exercise].sets[set].videoUrl = downloadUrl;
                  delete formatedExercies[exercise].sets[set].videoBlob;
                })
                .catch((err) => {
                  console.error(err);
                });
            })
            .catch((err) => {
              suc = false;
              console.error(err);
            });
        }
      }
    }

    workoutData.exercises = formatedExercies;

    // Set Workout doc in users /workouts collection
    batch.set(userDocRef.collection("workouts").doc(docTitle), workoutData);

    // Sets Workout Data for user in /exercise collection
    for (const key in workoutData.exercises) {
      const exerciseData: any = {};
      exerciseData[docTitle] = {
        workoutName: workoutData.workoutName,
        sets: workoutData.exercises[key].sets,
      };

      // Example Name: Squat | Barbell
      const exerciseDocName = formatExerciseId(
        workoutData.exercises[key].exerciseName,
        workoutData.exercises[key].type
      );

      const exerciseRef = userDocRef
        .collection("exercises")
        .doc(exerciseDocName);
      // adds workout doc to /exercise/{exerciseName}/workouts
      batch.set(exerciseRef.collection("workouts").doc(docTitle), exerciseData);

      // updates /exercise/{exerciseName} datesDone[] field
      batch.update(exerciseRef, {
        datesDone: firebase.firestore.FieldValue.arrayUnion(docTitle),
      });
    }

    // update the workoutTemplate under userData
    if (payload.templateId) {
      const workoutDate: number = payload.workoutData.startTime;
      const userData: userData = rootGetters["userStore/getUserData"];

      // updated template data
      const newTemplateData: userWorkoutTemplate =
        userData.workoutTemplates[payload.templateId];
      newTemplateData.lastDone = workoutDate;

      if (newTemplateData.datesDone) {
        newTemplateData.datesDone.push(workoutDate);
        const nonDups = [...new Set(newTemplateData.datesDone)];
        newTemplateData.datesDone = nonDups;
      } else newTemplateData.datesDone = [workoutDate];

      newTemplateData.timesDone += 1;
      delete newTemplateData.currentlyDoingWorkout;

      const workoutTemplatesRef = `workoutTemplates.${payload.templateId}`;

      console.log(newTemplateData);

      batch.update(userDocRef, {
        [workoutTemplatesRef]: newTemplateData,
      });
    }

    await batch.commit().catch(async (err) => {
      // Deletes videos from workout that could not be uploaded
      for (const videoRef in allVideoLocations) {
        const videoLocation: string = allVideoLocations[videoRef];
        await firebaseApp
          .storage()
          .ref(videoLocation)
          .delete()
          .then(() => {
            console.log(
              "DELETED VIDEO FROM WORKOUT THAT COULD NOT BE UPLOADED "
            );
          })
          .catch(() => {
            console.error("COULD NOT DELETE VIDEO FROM FAILED WORKOUT UPLOAD");
          });
      }

      console.error(err);
      suc = false;
    });

    if (suc) return Promise.resolve(workoutData);
    return Promise.reject("Something Went Wrong");
  },
  async deleteWorkout({ rootGetters, commit }, payload: Workout) {
    let suc = true;
    const myUID: string | null = rootGetters["userStore/getUserUID"];
    if (myUID !== null) {
      const batch = firebaseApp.firestore().batch();
      const userDocRef = firebaseApp
        .firestore()
        .collection("users")
        .doc(myUID);

      const exerciseRef = userDocRef.collection("exercises");

      const workoutDocId: string = formatWorkoutDocId(payload.startTime);
      // Delete from workout/workoutDoc
      batch.delete(userDocRef.collection("workouts").doc(workoutDocId));

      // Delete from exercise/${exerciseName}/exerciseWorkouts
      payload.exercises.forEach((exercise) => {
        const exerciseId: string = formatExerciseId(
          exercise.exerciseName,
          exercise.type
        );

        // Delete from /exercise/{exericseName}/workouts
        batch.delete(
          exerciseRef
            .doc(exerciseId)
            .collection("workouts")
            .doc(workoutDocId)
        );
        // Delete date from /exercise/{exericseName} datesDone[]
        batch.update(exerciseRef.doc(exerciseId), {
          datesDone: firebase.firestore.FieldValue.arrayRemove(workoutDocId),
        });
      });

      // Delete videos
      for (const exercise in payload.exercises) {
        const setsIdxWithVideos: number[] = [];

        for (const set in payload.exercises[exercise]) {
          const exerciseSets = payload.exercises[exercise].sets;
          exerciseSets.forEach((set, setIdx) => {
            if (set.videoUrl && !setsIdxWithVideos.includes(setIdx))
              setsIdxWithVideos.push(setIdx);
          });
        }
        for (const setIdx in setsIdxWithVideos) {
          await firebaseApp
            .storage()
            .ref(
              `users/${myUID}/workouts/exercises/${formatExerciseId(
                payload.exercises[exercise].exerciseName,
                payload.exercises[exercise].type
              )}/${formatExerciseVideoId(
                payload.startTime,
                setsIdxWithVideos[setIdx]
              )}`
            )
            .delete()
            .catch((err) => {
              suc = false;
            });
        }
      }

      await batch.commit().catch((err) => {
        console.error(err);
        suc = false;
      });

      if (suc) {
        commit("historyStore/removeWorkout", payload, {
          root: true,
        });
        return Promise.resolve();
      }
      return Promise.reject("Something Went Wrong");
    } else return Promise.reject("User Not Signed in.");
  },
  async updateCurrentWorkout({ rootGetters }, payload: Workout | null) {
    let suc = true;
    const myUID: string | null = rootGetters["userStore/getUserUID"];
    if (myUID !== null) {
      const userDocRef = firebaseApp
        .firestore()
        .collection("users")
        .doc(myUID);

      await userDocRef
        .update({
          currentWorkout: payload,
        })
        .catch(() => {
          suc = false;
        });

      if (!suc)
        return Promise.reject("Could not update users current workout ");
      else return Promise.resolve("Updated users current workout.");
    } else return Promise.reject("User Not Signed in.");
  },
};

const workoutStore = {
  namespace: true,
  state: new State(),
  getters: getters,
  mutations: mutations,
  actions: actions,
};

export default workoutStore;
